import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";
import { toast } from "sonner";
import {
    LayoutDashboard,
    Users,
    Clock,
    Calendar,
    Video,
    Megaphone,
    FolderKanban,
    CheckSquare,
    Coins,
    FileSpreadsheet,
    Building2,
    LogOut,
    Menu,
    X,
    ChevronDown,
    ChevronRight,
    GraduationCap,
    Award,
    FileText,
    ThumbsUp,
    FileSignature,
    ShieldAlert,
    History,
    Layout,
    Settings,
    MapPin,
    MessageSquare,
    CreditCard,
    Shield,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Sun, Moon } from "lucide-react";

const DOCS_NAV = [
    { to: "/interns", label: "Interns", icon: GraduationCap },
    { to: "/documents/certificates", label: "Certificates", icon: Award },
    { to: "/documents/experience", label: "Experience Letters", icon: FileText },
    { to: "/documents/recommendation", label: "Recommendation Letters", icon: ThumbsUp },
    { to: "/documents/offers", label: "Offer Letters", icon: FileSignature },
    { to: "/documents/ndas", label: "NDA Documents", icon: ShieldAlert },
    { to: "/documents/logs", label: "Verification Logs", icon: History },
    { to: "/documents/templates", label: "Templates", icon: Layout },
    { to: "/documents/settings", label: "Doc Settings", icon: Settings },
];

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/employees", label: "Employees", icon: Users, roles: ["super_admin", "hr", "manager", "lead"] },
    { to: "/alumni", label: "Alumni", icon: GraduationCap, roles: ["super_admin", "hr", "manager", "lead"] },
    { to: "/attendance", label: "Attendance", icon: Clock, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/leaves", label: "Leaves", icon: Calendar, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/meetings", label: "Meetings", icon: Video, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/notifications", label: "Notices", icon: Megaphone, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/projects", label: "Projects", icon: FolderKanban, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/tasks", label: "Tasks", icon: CheckSquare, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/payroll", label: "Payroll", icon: Coins, roles: ["super_admin"] },
    { to: "/billing", label: "Billing & Plans", icon: CreditCard, roles: ["super_admin", "hr"] },
    { to: "/admin-studio", label: "Admin Studio", icon: Shield, roles: ["super_admin"] },
    { to: "/forms", label: "Forms", icon: FileSpreadsheet, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
    { to: "/tracking", label: "Live Tracking", icon: MapPin, roles: ["super_admin"] },
    { to: "/team", label: "My team", icon: Users, roles: ["super_admin", "hr", "manager", "lead"] },
    { to: "/chat", label: "Chat", icon: MessageSquare, roles: ["super_admin", "hr", "manager", "lead", "employee", "intern"] },
];

export default function Sidebar({ open, onClose }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [docsOpen, setDocsOpen] = useState(false);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const prevCountRef = useRef(0);

    const playBeep = () => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(587.33, ctx.currentTime);
            osc.frequency.setValueAtTime(880, ctx.currentTime + 0.08);
            gain.gain.setValueAtTime(0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.3);
        } catch (e) {
            console.error("Audio Context failed:", e);
        }
    };

    useEffect(() => {
        if (!user) return;
        
        let mounted = true;
        const checkUnread = async () => {
            try {
                const { data } = await api.get("/chat/unread-count");
                if (!mounted) return;
                
                const count = data.unread_count || 0;
                if (count > prevCountRef.current && location.pathname !== "/chat") {
                    playBeep();
                    toast.info("New message received in Chat", {
                        action: {
                            label: "View Chat",
                            onClick: () => navigate("/chat")
                        }
                    });
                }
                setUnreadChatCount(count);
                prevCountRef.current = count;
            } catch (err) {
                console.error("Failed to check unread count:", err);
            }
        };

        checkUnread();
        const interval = setInterval(checkUnread, 5000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [user, location.pathname, navigate]);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const visible = NAV.filter((n) => {
        if (n.to === "/admin-studio") {
            return user?.email === "admin@parshwebcraft.in";
        }
        return !n.roles || n.roles.includes(user?.role);
    });

    return (
        <>
            {/* mobile backdrop */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
                        data-testid="sidebar-backdrop"
                    />
                )}
            </AnimatePresence>

            <aside
                className={`fixed left-0 top-0 h-screen w-72 z-40
                bg-white/80 dark:bg-[hsl(var(--surface))]/70 backdrop-blur-2xl backdrop-saturate-150
                border-r border-black/5 dark:border-white/10
                flex flex-col
                transition-transform duration-300
                ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
                data-testid="sidebar"
            >
                <div className="flex items-center justify-between px-6 h-16 border-b border-black/5 dark:border-white/10">
                    <NavLink to="/dashboard" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-white dark:bg-white grid place-items-center shadow-[0_0_20px_rgba(13,148,136,0.15)] overflow-hidden p-1">
                            <img src="/logo.png" alt="ParshHR Logo" className="w-full h-full object-contain" />
                        </div>
                        <div className="font-heading font-bold text-xl tracking-tight text-slate-800 dark:text-slate-100">ParshHR</div>
                    </NavLink>
                    <button
                        onClick={onClose}
                        className="lg:hidden p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                        data-testid="sidebar-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-hide">
                    {visible.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={onClose}
                                className={({ isActive }) =>
                                    `flex items-center justify-between px-3 py-2.5 text-sm font-medium mb-1 transition-all
                                    ${isActive
                                        ? "bg-primary/10 dark:bg-primary/20 text-primary border-l-[4px] border-primary rounded-r-xl rounded-l-none pl-2.5 font-semibold"
                                        : "text-foreground/70 hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground rounded-xl"
                                    }`
                                }
                                data-testid={`nav-${item.to.replace("/", "")}`}
                            >
                                <div className="flex items-center gap-3">
                                    <Icon className="w-5 h-5" strokeWidth={isActive => isActive ? 2.2 : 1.7} />
                                    <span>{item.label}</span>
                                </div>
                                {item.to === "/chat" && unreadChatCount > 0 && (
                                    <span className="px-2 py-0.5 rounded-full bg-primary text-white text-[10px] font-bold shadow-md shadow-primary/20 animate-pulse">
                                        {unreadChatCount}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}

                    {/* HR Documents Collapsible Menu */}
                    {user?.role === "super_admin" && (
                        <div className="mt-4 border-t border-black/5 dark:border-white/5 pt-3">
                            <button
                                onClick={() => setDocsOpen(!docsOpen)}
                                className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium mb-1 transition-all text-foreground/70 hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground rounded-xl`}
                            >
                                <div className="flex items-center gap-3">
                                    <FileSpreadsheet className="w-5 h-5 text-primary" strokeWidth={1.7} />
                                    <span>HR Documents</span>
                                </div>
                                {docsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </button>
                            
                            <AnimatePresence>
                                {docsOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="overflow-hidden pl-3 space-y-0.5 mt-1"
                                    >
                                        {DOCS_NAV.map((subitem) => {
                                            const SubIcon = subitem.icon;
                                            return (
                                                <NavLink
                                                    key={subitem.to}
                                                    to={subitem.to}
                                                    onClick={onClose}
                                                    className={({ isActive }) =>
                                                        `flex items-center gap-3 px-3 py-1.5 text-[13px] font-medium transition-all
                                                        ${isActive
                                                            ? "bg-primary/10 dark:bg-primary/20 text-primary border-l-[3px] border-primary rounded-r-xl rounded-l-none pl-2 font-semibold"
                                                            : "text-foreground/60 hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground rounded-xl"
                                                        }`
                                                    }
                                                >
                                                    <SubIcon className="w-4 h-4" strokeWidth={1.7} />
                                                    <span>{subitem.label}</span>
                                                </NavLink>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </nav>

                <div className="p-4 border-t border-black/5 dark:border-white/10 space-y-4">
                    <button
                        onClick={toggle}
                        className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl text-xs text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 transition"
                        data-testid="theme-toggle"
                    >
                        {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                        <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
                    </button>

                    <div 
                        onClick={() => navigate("/profile")}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition"
                    >
                        {user?.avatar ? (
                            <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full object-cover shadow-sm" />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-emerald-400 grid place-items-center text-white font-semibold shadow-sm">
                                {user?.name?.[0]?.toUpperCase()}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-foreground truncate">{user?.name || "Gauransh Jaroli"}</div>
                            <div className="text-[11px] text-muted-foreground truncate font-medium">
                                {user?.role === "super_admin" ? "Administrator" : user?.role?.replace("_", " ")}
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition"
                        data-testid="logout-button"
                    >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                    </button>
                </div>
            </aside>
        </>
    );
}
